import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import './CheckoutSuccess.css';

function CheckoutSuccess() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const hasExecutedRef = useRef(false);
  const isGerman = i18n.language === 'de';
  
  // Get data from location.state (for bank transfer) or URL params (for PayPal)
  const stateData = location.state || {};
  const paymentId = searchParams.get('paymentId');
  const token = searchParams.get('token');
  const payerId = searchParams.get('PayerID');
  const orderId = searchParams.get('orderId');
  
  useEffect(() => {
    // If we have PayPal params, this is a onetime payment that needs to be executed
    if (paymentId && payerId && orderId && !hasExecutedRef.current) {
      hasExecutedRef.current = true;
      executePayPalPayment();
    } else if (paymentId || token) {
      // Just display success for completed transactions
      setOrderDetails({
        planName: 'Proxy-Karte',
        amount: '0,50',
        paymentMethod: 'PayPal',
        isOnetime: true
      });
    } else {
      // Use state data for subscriptions/bank transfer
      setOrderDetails({
        planName: stateData.planName,
        amount: stateData.amount,
        paymentMethod: stateData.paymentMethod || 'PayPal',
        isOnetime: stateData.isOnetime || false
      });
    }
  }, []);
  
  const executePayPalPayment = async () => {
    setIsProcessing(true);
    try {
      const response = await api.request('/api/paypal/execute-payment', {
        method: 'POST',
        body: JSON.stringify({
          payment_id: paymentId,
          payer_id: payerId,
          order_id: orderId
        })
      });
      
      if (response && response.success) {
        const order = await api.getOrder(orderId);
        
        setOrderDetails({
          planName: order.items[0]?.product_name || 'Proxy-Karte',
          amount: (order.total_amount || 0).toFixed(2).replace('.', ','),
          paymentMethod: 'PayPal',
          isOnetime: order.order_type === 'onetime'
        });
      }
    } catch (err) {
      console.error('Error executing PayPal payment:', err);
      
      try {
        const order = await api.getOrder(orderId);
        
        if (order.status === 'paid' || order.payment_status === 'paid') {
          setOrderDetails({
            planName: order.items[0]?.product_name || 'Proxy-Karte',
            amount: (order.total_amount || 0).toFixed(2).replace('.', ','),
            paymentMethod: 'PayPal',
            isOnetime: order.order_type === 'onetime'
          });
          return;
        }
      } catch (orderErr) {
        console.error('Error fetching order:', orderErr);
      }
      
      setError(isGerman 
        ? 'Fehler beim Abschluss der Zahlung. Bitte kontaktiere den Support.'
        : 'Error completing payment. Please contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="checkout-success-page">
        <div className="success-container">
          <div className="spinner"></div>
          <p>{t('checkout.processing')}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="checkout-success-page">
        <div className="success-container">
          <div className="error-icon">❌</div>
          <h1 className="error-title">{t('checkoutError.title')}</h1>
          <p className="error-message">{error}</p>
          <button 
            onClick={() => navigate('/dashboard')} 
            className="btn-primary"
          >
            {t('checkoutSuccess.toDashboard')}
          </button>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="checkout-success-page">
        <div className="success-container">
          <div className="spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success-page" data-testid="checkout-success-page">
      <div className="success-container">
        <div className="success-icon">✅</div>
        <h1 className="success-title">{t('checkoutSuccess.title')}</h1>
        <p className="success-message">
          {orderDetails.isOnetime 
            ? (isGerman 
                ? `Vielen Dank für deinen Kauf! Du hast ${orderDetails.planName} erfolgreich erworben.`
                : `Thank you for your purchase! You have successfully purchased ${orderDetails.planName}.`)
            : (isGerman
                ? `Vielen Dank für deine Bestellung. Dein ${orderDetails.planName}-Abonnement ist jetzt aktiv.`
                : `Thank you for your order. Your ${orderDetails.planName} subscription is now active.`)
          }
        </p>

        <div className="success-details">
          <div className="detail-row">
            <span className="detail-label">{orderDetails.isOnetime ? (isGerman ? 'Produkt:' : 'Product:') : (isGerman ? 'Plan:' : 'Plan:')}</span>
            <span className="detail-value">{orderDetails.planName}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{isGerman ? 'Betrag:' : 'Amount:'}</span>
            <span className="detail-value">{orderDetails.amount}€</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{isGerman ? 'Zahlungsmethode:' : 'Payment method:'}</span>
            <span className="detail-value">{orderDetails.paymentMethod}</span>
          </div>
        </div>

        <div className="success-info">
          <p>📧 {isGerman ? 'Eine Bestätigungs-E-Mail wurde an deine E-Mail-Adresse gesendet.' : 'A confirmation email has been sent to your email address.'}</p>
          {orderDetails.isOnetime 
            ? <p>🎉 {isGerman ? 'Du kannst dein Produkt jetzt nutzen!' : 'You can now use your product!'}</p>
            : <p>🎉 {t('checkoutSuccess.enjoyZenta')}</p>
          }
        </div>

        <div className="success-actions">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="btn-primary"
            data-testid="go-to-dashboard-button"
          >
            {t('checkoutSuccess.toDashboard')}
          </button>
          <button 
            onClick={() => navigate('/pricing')} 
            className="btn-secondary"
            data-testid="back-to-pricing-button"
          >
            {t('bankTransfer.backToPricing')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CheckoutSuccess;
